import { cn } from "@/lib/utils";

/**
 * The gate, made legible: the model's confidence against the threshold it
 * missed.
 *
 * Two markers rather than one bar, because "0.42" means nothing without "the
 * gate for a venue is 0.75". The fill is red whenever confidence is below the
 * threshold — which, on a parked job, it always is; the amber/emerald arms
 * exist so the same component can render a *resolved* or retried job honestly.
 */
export function ConfidenceMeter({
  confidence,
  threshold,
  className,
}: {
  confidence: number | null | undefined;
  threshold: number | null | undefined;
  className?: string;
}) {
  const hasConfidence = typeof confidence === "number" && confidence >= 0;
  const hasThreshold = typeof threshold === "number" && threshold >= 0;

  if (!hasConfidence) {
    return (
      <span className={cn("text-xs text-muted-foreground", className)}>
        no confidence reported
      </span>
    );
  }

  const value = clamp(confidence);
  const gate = hasThreshold ? clamp(threshold) : null;
  const below = gate !== null && value < gate;

  return (
    <div className={cn("flex min-w-[8rem] flex-col gap-1", className)}>
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full",
            below ? "bg-red-500/70" : "bg-emerald-500/70",
          )}
          style={{ width: `${value * 100}%` }}
        />
        {gate !== null && (
          <div
            aria-hidden
            className="absolute inset-y-0 w-px bg-foreground/70"
            style={{ left: `${gate * 100}%` }}
          />
        )}
      </div>
      <div className="flex items-baseline gap-1.5 text-[11px] tabular-nums">
        <span
          className={cn(
            "font-medium",
            below && "text-red-600 dark:text-red-400",
          )}
        >
          {percent(value)}
        </span>
        {gate !== null && (
          <span className="text-muted-foreground">
            {below ? "below" : "vs"} gate {percent(gate)}
          </span>
        )}
      </div>
    </div>
  );
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}
